import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Per-device "most used" ranking for the m.idarah.io landing grid.
 *
 * Field staff open the same three or four destinations every shift, so the ones
 * they actually use are worth pinning to the top. Counts live in localStorage,
 * scoped per user id, so two accounts sharing a handset never see each other's
 * history. Nothing leaves the device.
 */

const STORAGE_PREFIX = "mobile-home-frequent:";
/** Cap the stored map so a long-lived device cannot accumulate unbounded entries. */
const MAX_ENTRIES = 24;
const DEFAULT_LIMIT = 4;

interface FrequentEntry {
  count: number;
  /** Epoch ms of the last tap — breaks count ties in favour of recent use. */
  lastUsedAt: number;
}

type FrequentMap = Record<string, FrequentEntry>;

function readMap(storageKey: string): FrequentMap {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as FrequentMap;
  } catch {
    // Corrupt JSON, or localStorage blocked (private mode / disabled storage).
    return {};
  }
}

export function useFrequentTiles(userKey?: string | number, limit = DEFAULT_LIMIT) {
  const storageKey = `${STORAGE_PREFIX}${userKey ?? "anonymous"}`;
  const [entries, setEntries] = useState<FrequentMap>(() => readMap(storageKey));

  // A different account signing in on the same handset gets its own ranking.
  useEffect(() => {
    setEntries(readMap(storageKey));
  }, [storageKey]);

  const recordVisit = useCallback(
    (href: string) => {
      // Written synchronously, and read back from storage rather than from state:
      // tapping a tile navigates away at once, and a setState updater queued in the
      // click handler is discarded when this page unmounts — which silently lost the
      // first tap. localStorage is the source of truth; `entries` only mirrors it.
      const current = readMap(storageKey);
      const bounded: FrequentMap = Object.fromEntries(
        Object.entries({
          ...current,
          [href]: { count: (current[href]?.count ?? 0) + 1, lastUsedAt: Date.now() },
        })
          .sort((a, b) => b[1].lastUsedAt - a[1].lastUsedAt)
          .slice(0, MAX_ENTRIES),
      );
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(bounded));
      } catch {
        // Storage full or unavailable: the current session still updates in memory.
      }
      setEntries(bounded);
    },
    [storageKey],
  );

  const topHrefs = useMemo(
    () =>
      Object.entries(entries)
        .sort((a, b) => b[1].count - a[1].count || b[1].lastUsedAt - a[1].lastUsedAt)
        .slice(0, limit)
        .map(([href]) => href),
    [entries, limit],
  );

  return { topHrefs, recordVisit };
}
